import classNames from 'classnames';
import { PopupDirection, PopupTarget, Position } from 'lego-on-react';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { ThemePopup } from '../../typings/common';
import { ContainerOverflow } from '../ContainerOverflow/ContainerOverflow';
import { ScopedPopup } from '../ScopedPopup/ScopedPopup';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';

import './PopupDecorator.css';

export enum PopupDirections {
    RIGHT_TOP = 'right-top',
    RIGHT_BOTTOM = 'right-bottom',
    TOP_LEFT = 'top-left',
    TOP_RIGHT = 'top-right',
    BOTTOM_RIGHT = 'bottom-right',
    BOTTOM_LEFT = 'bottom-left'
}

export interface PopupDecoratorProps {
    hasClose?: boolean;
    onClose?: () => void;
    cls?: string;
    popupHint?: string;
    onOutsideClick?: () => void;
    anchor?: React.ReactNode;
    visible: boolean;
    footer?: React.ReactNode;
    header?: string;
    directions?: Array<PopupDirection>;
    hasTail?: boolean;
    target: PopupTarget;
    position?: Position;
    maxBodyHeight?: number;
    theme?: ThemePopup;
    zIndexGroupLevel?: number;
}

export class PopupDecorator extends React.Component<PopupDecoratorProps> {
    render() {
        const {
            hasClose,
            maxBodyHeight,
            visible,
            anchor,
            onOutsideClick,
            cls,
            directions,
            hasTail,
            position,
            target,
            theme = ThemePopup.DEFUALT,
            zIndexGroupLevel
        } = this.props;

        return (
            <ScopedPopup
                cls={classNames(
                    cls,
                    Bem.cls({
                        block: 'popup-decorator',
                        mods: { theme, scrollable: maxBodyHeight !== undefined }
                    })
                )}
                directions={directions}
                hasTail={hasTail}
                theme="normal"
                view={'default'}
                position={position}
                visible={visible}
                anchor={anchor}
                autoclosable={true}
                onOutsideClick={onOutsideClick}
                target={target}
                zIndexGroupLevel={zIndexGroupLevel}
            >
                {hasClose ? (
                    <span
                        className={Bem.cls('popup-decorator', 'close')}
                        onClick={this.onClose}
                    >
                        <Shape2
                            width={16}
                            height={16}
                            icon={Shape2Icon.close}
                            theme={Shape2Theme.light}
                        />
                    </span>
                ) : null}

                {this.getHeader()}

                {this.getBody()}

                {this.getFooter()}
            </ScopedPopup>
        );
    }

    private getBody() {
        const { children, maxBodyHeight } = this.props;

        if (maxBodyHeight) {
            return (
                <div
                    style={{ maxHeight: maxBodyHeight }}
                    className={Bem.cls('popup-decorator', 'scrollable-body')}
                >
                    <ContainerOverflow>
                        <div
                            className={Bem.cls(
                                'popup-decorator',
                                'scrollable-body-content'
                            )}
                        >
                            {children}
                        </div>
                    </ContainerOverflow>
                </div>
            );
        }

        return (
            <div className={Bem.cls('popup-decorator', 'body')}>{children}</div>
        );
    }

    private getFooter() {
        const { footer } = this.props;

        if (!footer) {
            return null;
        }

        return (
            <div className={Bem.cls('popup-decorator', 'footer')}>{footer}</div>
        );
    }

    private getHeader() {
        const { header, maxBodyHeight } = this.props;

        if (!header) {
            return null;
        }

        return (
            <div
                className={Bem.cls('popup-decorator', 'header', {
                    maxBodyHeight: Boolean(maxBodyHeight)
                })}
            >
                <div className={Bem.cls('popup-decorator', 'title')}>
                    {header}
                </div>

                {this.getPopupHint()}
            </div>
        );
    }

    private getPopupHint() {
        const { popupHint } = this.props;

        if (!popupHint) {
            return null;
        }

        return (
            <p className={Bem.cls('popup-decorator', 'header-hint')}>
                {popupHint}
            </p>
        );
    }

    private onClose = () => {
        const { onClose } = this.props;

        if (onClose) {
            onClose();
        }
    };
}
